import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import KeywordsMaker from "../utilities/KeywordsMaker";
import IconButton from "components/common/IconButton";
import PageHeader from "components/common/PageHeader";
import FalconComponentCard from "components/common/FalconComponentCard";
import audiences from "../../config/audiences";

const CreateUpcomingEventForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    brief: "",
    content: "",
    credit: "ECPL",
    keyword1: "",
    keyword2: "",
    keyword3: "",
    image_file: null,
    featured: false,
    enabled: true,
    post_date: null,
    audience: "public",
    startDateDD: null,
    startDateMM: null,
    startDateYYYY: null,
    endDateDD: null,
    endDateMM: null,
    endDateYYYY: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      const eventId = window.location.pathname.split("/").pop();
      if (eventId) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;

          const response = await axios.get(
            `${apiUrl}/upcoming-events/id/${eventId}`
          );

          if (response.data) {
            // console.log('responded', response.data);
            const eventData = response.data.data[0];
            console.log("eventData", eventData);
            const keyWordsSeparated = eventData.keywords
              ? eventData.keywords.split("; ")
              : ["", "", ""];

            setFormData({
              title: eventData.title,
              brief: eventData.brief,
              content: eventData.content,
              credit: eventData.credit,
              image_file: eventData.image_file,
              image_filename: eventData.image_filename,
              keyword1: keyWordsSeparated[0],
              keyword2: keyWordsSeparated[1],
              keyword3: keyWordsSeparated[2],
              date_added: eventData.date_added,

              startDateDD: eventData.start_date.split("/")[0],
              startDateMM: eventData.start_date.split("/")[1],
              startDateYYYY: eventData.start_date.split("/")[2],
              endDateDD: eventData.end_date.split("/")[0],
              endDateMM: eventData.end_date.split("/")[1],
              endDateYYYY: eventData.end_date.split("/")[2],

              featured: eventData.featured === "false" ? false : true,
              enabled: eventData.enabled === "false" ? false : true,
              audience: eventData.audience,
            });
          }
        } catch (err) {
          console.log("Error", err);
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/upcoming-events/list");
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const startDate = `${formData.startDateDD}/${formData.startDateMM}/${formData.startDateYYYY}`;
    const endDate = `${formData.endDateDD}/${formData.endDateMM}/${formData.endDateYYYY}`;

    const { startDateDD, startDateMM, startDateYYYY } = formData;
    const { endDateDD, endDateMM, endDateYYYY } = formData;

    const inputStartDate = new Date(
      startDateYYYY,
      startDateMM - 1,
      startDateDD
    );
    const inputEndDate = new Date(endDateYYYY, endDateMM - 1, endDateDD);

    const today = new Date();
    today.setHours(0, 0, 0, 0); // set time to 00:00:00.000

    try {
      if (
        inputEndDate < inputStartDate ||
        startDateMM > 12 ||
        startDateDD > 31 ||
        endDateMM > 12 ||
        endDateDD > 31
      ) {
        toast.error("Post/Expiry dates are invalid.");
        return;
      }
    } catch (error) {
      toast.error("Post/Expiry dates are invalid.");
      return;
    }

    const keywordsCombined = KeywordsMaker([
      formData.keyword1,
      formData.keyword2,
      formData.keyword3,
    ]);

    console.log("keywordsCombined", keywordsCombined);

    if (moment(formData.date_expiry).isBefore(formData.date_added)) {
      toast.error(`Expiry date must be after date added`, {
        theme: "colored",
      });
      return;
    }

    let formData2 = new FormData();
    formData2.append("title", formData.title);
    formData2.append("brief", formData.brief);
    formData2.append("content", formData.content);
    formData2.append("credit", formData.credit);
    formData2.append("keywords", keywordsCombined);
    formData2.append("date_updated", moment.utc().format("YYYY-MM-DD HH:mm"));
    formData2.append("start_date", startDate);
    formData2.append("end_date", endDate);
    formData2.append("featured", formData.featured);
    formData2.append("audience", formData.audience);
    formData2.append("enabled", formData.enabled);
    formData2.append("image_file", formData.image_file);
    formData2.append("image_filename", formData.image_filename);

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}`;
      const id = window.location.pathname.split("/").pop();

      const response = await axios.put(
        `${apiUrl}/upcoming-events/id/${id}`,
        formData2
      );

      if (response) {
        console.log("responded", response.statusText);
        toast.success(`Upcoming event uploaded successfully`, {
          theme: "colored",
        });
      }
    } catch (err) {
      // console.log('Error', err);
      toast.error(
        err.response?.data?.message ?? "Error saving upcoming event",
        {
          theme: "colored",
          autoClose: false,
          closeButton: true,
          hideProgressBar: true,
        }
      );
    }
  };

  const handleFieldChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file") {
      const image_file = files[0];
      setFormData({
        ...formData,
        image_file,
        image_filename: image_file.name,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Edit an upcoming event"
        description="This upcoming event will be listed on the Upcoming Events page. The latest upcoming event will be displayed on the home page, in the Upcoming Events section."
        className="mb-3"
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title="Details"
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        {!loading && (
          <Form onSubmit={handleSubmit}>
            <FalconComponentCard.Body>
              <Form.Label>Title</Form.Label>
              <Form.Control
                size="lg"
                type="text"
                placeholder=""
                name="title"
                onChange={handleFieldChange}
                required
                autoComplete="off"
                value={formData.title}
              />
              <br />
              <Form.Label>Author</Form.Label>
              <Form.Control
                size="lg"
                type="text"
                placeholder=""
                name="credit"
                onChange={handleFieldChange}
                readOnly
                defaultValue={formData.credit}
                required
              />
              <br />
              <Form.Label>Brief</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="brief"
                onChange={handleFieldChange}
                autoComplete="off"
                value={formData.brief}
                required
              />
              <br />
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="content"
                onChange={handleFieldChange}
                autoComplete="off"
                required
                value={formData.content}
              />
              <br />

              <Row>
                <Col lg="6">
                  <Link to={formData.image_file} target="_blank">
                    View Current Picture
                  </Link>
                  <br />
                  <br />
                  <Form.Group controlId="formFileLg" className="mb-3">
                    <Form.Label>Select new picture</Form.Label>
                    <Form.Control
                      type="file"
                      size="lg"
                      name="file"
                      onChange={handleFieldChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lang="4">
                  <Form.Group
                    controlId="formFileLg"
                    className="mb-3"
                    width="100%"
                  >
                    <Form.Label>Post date</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="DD"
                          name="startDateDD"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startDateDD}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="MM"
                          name="startDateMM"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startDateMM}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="4"
                          size="lg"
                          type="text"
                          placeholder="YYYY"
                          name="startDateYYYY"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startDateYYYY}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
                <Col lang="2"></Col>

                <Col lang="4">
                  <Form.Group
                    controlId="formFileLg"
                    className="mb-3"
                    width="100%"
                  >
                    <Form.Label>Expiry date</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="DD"
                          name="endDateDD"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endDateDD}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="MM"
                          name="endDateMM"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endDateMM}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="4"
                          size="lg"
                          type="text"
                          placeholder="YYYY"
                          name="endDateYYYY"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endDateYYYY}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <Form.Group>
                    <Form.Label>Audience</Form.Label>
                    <Form.Select
                      size="md"
                      className="mb-3"
                      name="audience"
                      onChange={handleFieldChange}
                      value={formData.audience}
                    >
                      {audiences.map((audience) => (
                        <option key={audience.value} value={audience.value}>
                          {audience.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg="6">
                  <Form.Group>
                    <Form.Check
                      name="featured"
                      type="checkbox"
                      id="featuredCheckbox"
                      label="Featured"
                      onChange={(e) =>
                        setFormData({ ...formData, featured: e.target.checked })
                      }
                      checked={formData.featured}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Check
                      name="enabled"
                      type="checkbox"
                      id="featuredCheckbox"
                      label="Enabled"
                      onChange={(e) =>
                        setFormData({ ...formData, enabled: e.target.checked })
                      }
                      checked={formData.enabled}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg="4">
                  <Form.Label>Keyword 1</Form.Label>
                  <Form.Control
                    size="lg"
                    type="text"
                    placeholder=""
                    name="keyword1"
                    onChange={handleFieldChange}
                    defaultValue={formData.keyword1}
                  />
                </Col>
                <Col lg="4">
                  <Form.Label>Keyword 2</Form.Label>
                  <Form.Control
                    size="lg"
                    type="text"
                    placeholder=""
                    name="keyword2"
                    onChange={handleFieldChange}
                    defaultValue={formData.keyword2}
                  />
                </Col>
                <Col lg="4">
                  <Form.Label>Keyword 3</Form.Label>
                  <Form.Control
                    size="lg"
                    type="text"
                    placeholder=""
                    name="keyword3"
                    onChange={handleFieldChange}
                    defaultValue={formData.keyword3}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg="6" className="mt-4 mb-3 ml-3">
                  <Form.Group>
                    <IconButton
                      className="mb-1"
                      variant="falcon-default"
                      size="lg"
                      icon="plus"
                      transform="shrink-3"
                      type="submit"
                    >
                      Submit
                    </IconButton>
                  </Form.Group>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </Form>
        )}
      </FalconComponentCard>
    </>
  );
};

export default CreateUpcomingEventForm;
